import {t} from "@lingui/core/macro";

import {noTrans} from "@atg-shared/no-trans";
import {
    InvitationSetting,
    MembershipPolicy,
    TeamSettingsUpdateMethod,
    PreBookedErrorCode,
    TeamSettingsErrorCode,
    TeamSettingsSuccessCode,
    TeamPageLink,
} from "@atg-tillsammans/types";
import {Paths} from "@atg-tillsammans-shared/navigation";

import * as TeamUtils from "./teamUtils";

import type {ApolloError} from "@apollo/client";
import type {Team, TeamID} from "@atg-tillsammans/types";

export const getTeamNavbarInfo = (teamId: TeamID) => ({
    activeBettersPath: Paths.getTeamSubpagePath(teamId, "spellaggare"),
    subscriptionPath: Paths.getTeamSubpagePath(teamId, "foranmalningar"),
    membersPath: Paths.getTeamSubpagePath(teamId, "medlemmar"),
    emailPath: Paths.getTeamSubpagePath(teamId, "epost"),
});

export const isChatEnabled = (path: string) => {
    const pages = [
        "bjud-in",
        "spellaggare",
        "foranmalningar",
        "laginstallningar",
        "laginformation",
        "medlemmar",
        "epost",
    ];

    const regexp = new RegExp(
        `^(\\/utloggad|\\/${
            Paths.TILLSAMMANS_NAMESPACE
        })?\\/lagsida\\/\\d+\\/(${pages.join("|")})$`,
        "g",
    );
    return regexp.test(path);
};

export const getPageTitle = (pathname: string) => {
    const path = pathname.substring(pathname.lastIndexOf("/") + 1);

    if (Paths.isTeamRoundPage(pathname)) {
        let gameType = path.split("_")[0];
        if (gameType === "BIG9") gameType = noTrans("Big 9");
        return t({
            id: `team.utils.teamRoundPage.pageTitle`,
            message: `Spela ${gameType}`,
        });
    }

    switch (path) {
        case "medlemmar":
            return t`Medlemmar`;
        case "foranmalningar":
            return t`Föranmälningar`;
        case "laginstallningar":
            return t`Laginställningar`;
        case "epost":
            return t`Skicka e-post`;
        case "spellaggare":
            return t`Spelläggare`;
        case "bjud-in":
            return t`Bjud in`;
        case "laginformation":
            return t`Laginformation`;
        default: {
            return t`Lagsida`;
        }
    }
};

type TeamPageInfo = {
    chatEnabled: boolean;
};

export const TEAM_PAGE_INFO = {chatEnabled: true};
export const TEAM_ROUND_PAGE_INFO = {chatEnabled: true};
export const TEAM_SUBPAGE_INFO = {chatEnabled: false};

export const getTeamPageInfo = (path: string): TeamPageInfo => ({
    chatEnabled:
        Paths.isTeamBasePage(path) || Paths.isTeamRoundPage(path) || isChatEnabled(path),
});

export const formatInviteType = (inviteType: string) => {
    switch (inviteType) {
        case InvitationSetting.ALL: {
            return {
                label: t`Alla medlemmar`,
                description: t`Alla medlemmar kan bjuda in nya personer till laget.`,
                value: InvitationSetting.ALL,
            };
        }
        default:
            return {
                label: t`Bara lagkapten`,
                description: t`Endast lagkaptenen kan bjuda in nya personer till laget.`,
                value: InvitationSetting.TEAM_CAPTAIN,
            };
    }
};

export const formatMembershipSelection = (policy: string, isApp = false) => {
    switch (policy) {
        case MembershipPolicy.OPEN: {
            return {
                label: t`Öppet`,
                text: isApp
                    ? t`Alla kan hitta och gå med i laget direkt.`
                    : t`Vem som helst kan gå med i laget antingen genom att söka upp och gå med i laget via atg.se/tillsammans/hittalag eller genom att gå med via en inbjudningslänk.`,
            };
        }
        case MembershipPolicy.CLOSED: {
            return {
                label: t`Slutet`,
                text: isApp
                    ? t`Alla kan hitta laget men behöver ansöka om att gå med.`
                    : t`Medlemmar kan gå med i laget via en inbjudningslänk eller genom att söka upp och ansöka till laget via atg.se/tillsammans/hittalag. Det är bara lagkaptenen som kan godkänna en lagansökan.`,
            };
        }
        case MembershipPolicy.PRIVATE: {
            return {
                label: t`Privat`,
                text: isApp
                    ? t`Endast inbjudna kan gå med i laget. Laget syns inte i sökningar.`
                    : t`Medlemmar kan endast gå med i ett privat lag via en inbjudningslänk. Ett privat lag listas inte på atg.se/tillsammans/hittalag och det är heller inte sökbart.`,
            };
        }
        default:
            return {};
    }
};

export const formatMembershipPolicyText = (
    membershipPolicy: MembershipPolicy,
    isApp?: boolean,
): string => {
    const selection = formatMembershipSelection(membershipPolicy, isApp);
    return `${selection.label}. ${selection.text}`;
};

/**
 * Formats the extra membership policy content showing location visibility
 * @param team - Team object with location visibility setting
 * @param isCaptain - Whether current user is team captain
 * @param membershipPolicy - Current membership policy
 * @returns Formatted location visibility text or undefined if not applicable
 */
export const formatLocationVisibilityText = (
    team: {locationVisible: boolean},
    isCaptain: boolean,
    membershipPolicy: MembershipPolicy,
): string | undefined => {
    const shouldShowLocationVisibility =
        isCaptain && membershipPolicy !== MembershipPolicy.PRIVATE;

    if (!shouldShowLocationVisibility) {
        return undefined;
    }

    const visibilityStatus = team.locationVisible ? t`synlig` : t`dold`;
    return t`Hemorten är ${visibilityStatus}`;
};

export const getTeamErrorMessage = (
    errorCode?: string,
    updateMethod?: string,
): string => {
    switch (errorCode) {
        case TeamSettingsErrorCode.ACTIVE_ROUND_EXISTS:
            return t`Du kan inte ta bort ett lag som har en aktiv omgång.`;
        case TeamSettingsErrorCode.ACTIVE_ROUND_OF_GAME_TYPE_EXISTS:
            return t`Du kan inte välja bort en spelform där ni har en aktiv omgång. När omgången är avslutad kan du ändra.`;
        case TeamSettingsErrorCode.ACCESS_DENIED:
            if (updateMethod === TeamSettingsUpdateMethod.LEAVE_TEAM) {
                return t`För att kunna gå ur laget måste du logga in först.`;
            }
            if (updateMethod === TeamSettingsUpdateMethod.REMOVE_TEAM) {
                return t`För att kunna ta bort laget måste du logga in först.`;
            }
            return t`För att kunna göra ändringar måste du logga in först.`;
        case TeamSettingsErrorCode.NOT_MEMBER_OF_TEAM:
            if (updateMethod === TeamSettingsUpdateMethod.REMOVE_TEAM) {
                return t`Du kan inte ta bort laget då du inte längre är medlem.`;
            }
            return t`Du kan inte ändra detta eftersom du inte längre är medlem i det här laget.`;
        case TeamSettingsErrorCode.LACKS_ROLE:
            if (updateMethod === TeamSettingsUpdateMethod.REMOVE_TEAM) {
                return t`Du kan inte ta bort laget eftersom du inte är lagkapten.`;
            }
            return t`Du kan inte ändra detta eftersom du inte är lagkapten.`;
        case TeamSettingsErrorCode.TEAM_NAME_ALREADY_EXISTS:
            return t`Det här namnet är tyvärr upptaget. Pröva ett nytt.`;
        case TeamSettingsErrorCode.TEAM_ALREADY_EXISTS:
            return t`Laget existerar redan.`;
        case TeamSettingsErrorCode.MAX_NR_OF_TEAMS:
            return t`Vad roligt att du är så engagerad i Tillsammans, tyvärr har du nått gränsen för hur många lag du kan vara medlem i. För att skapa ett till lag måste du först gå ur ett annat.`;
        case TeamSettingsErrorCode.GAME_TYPE_LIST_IS_EMPTY:
            return t`Du måste välja minst en spelform.`;
        case TeamSettingsErrorCode.MEMBERS_EXISTS:
            return t`För att kunna ta bort laget måste du först ta bort alla medlemmar från laget.`;
        case TeamSettingsErrorCode.IS_CAPTAIN:
            return t`Du måste lämna över din roll som lagkapten till en annan medlem innan du kan gå ur laget.`;
        case TeamSettingsErrorCode.IS_GAME_INITIATOR:
            return t`Du kan inte gå ur ett lag där du är spelläggare för en aktiv omgång.`;
        case TeamSettingsErrorCode.IS_PARTICIPATING:
            return t`Eftersom du köpt en andel måste du vänta tills omgången är avgjord innan du kan gå ur laget.`;
        case TeamSettingsErrorCode.UPDATE_TEAM_DB_ERROR:
        default:
            return t`Tyvärr gick något fel hos oss. Försök igen om en stund!`;
    }
};

/**
 * This should probably be in teamUtils but since it is a js-file
 * we keep it here until we have refactored teamUtils.js to typescript
 */
export const getTeamSettingsErrorMessage = (
    apolloError?: ApolloError,
    updateMethod?: string,
): string => {
    const extensions = apolloError?.graphQLErrors[0]?.extensions;

    const errorCode = extensions
        ? (extensions.errorCode as TeamSettingsErrorCode)
        : TeamSettingsErrorCode.ACCESS_DENIED;

    return getTeamErrorMessage(errorCode, updateMethod);
};

export const getTeamSettingsSuccessMessage = (
    type: TeamSettingsSuccessCode,
    team?: Team,
): string => {
    const teamName = team ? team.name : "laget";
    switch (type) {
        case TeamSettingsSuccessCode.CLOSE_TEAM:
            return t`${teamName} är avslutat`;
        case TeamSettingsSuccessCode.LEAVE_TEAM:
            return t`Du är nu inte längre med i ${teamName}`;
        case TeamSettingsSuccessCode.DEFAULT:
        default:
            return t`Din ändring är sparad.`;
    }
};

export const getPreBookedErrorMessage = (apolloError?: ApolloError): string => {
    const extensions = apolloError?.graphQLErrors[0]?.extensions;

    const errorCode = extensions
        ? (extensions.errorCode as PreBookedErrorCode)
        : PreBookedErrorCode.ACCESS_DENIED;

    switch (errorCode) {
        case PreBookedErrorCode.ACCESS_DENIED:
            return t`Du behöver vara inloggad för att kunna sätta en föranmälan.`;
        case PreBookedErrorCode.NOT_MEMBER_OF_TEAM:
            return t`Du kan inte skapa en föranmälan då du inte är medlem i laget.`;
        case PreBookedErrorCode.RGS_SELF_EXCLUSION:
        case PreBookedErrorCode.RGS_TEMPORARY_SELF_EXCLUSION:
            return t`Föranmälan kan inte aktiveras. Ditt konto är spärrat för spel.`;
        case PreBookedErrorCode.RGS_NAR_EXCLUSION:
            return t`Din föranmälan kunde tyvärr inte genomföras. Enligt uppgifter från spelpaus.se har du stängt av dig för spel.`;
        case PreBookedErrorCode.RGS_NAR_UNDETERMINED:
            return t`Tjänsten spelpaus.se svarar inte just nu. Om du inte har stängt av dig för spel hos spelpaus.se vänligen logga ut och in på nytt.`;
        case PreBookedErrorCode.RGS_NETLOSS_LIMIT_EXCEEDED:
        case PreBookedErrorCode.RGS_NETLOSS_SERVER_ERROR:
            return t`Du har nått din förlustgräns. Kostnaden för spelet överskrider din satta förlustgräns.`;
        case PreBookedErrorCode.SUBSCRIPTION_NOT_FOUND:
        case PreBookedErrorCode.RGS_CASINO_HIGH_LOSS_LIMIT:
        case PreBookedErrorCode.RGS_CASINO_LOSS_LIMIT_EXCEEDED:
        case PreBookedErrorCode.RGS_CASINO_LOSS_LIMIT_NOT_SET:
        case PreBookedErrorCode.GENERAL_ERROR:
        default:
            return t`Tyvärr gick något fel hos oss. Försök igen om en stund!`;
    }
};

export const getRemovePreBookedErrorMessage = (apolloError?: ApolloError): string => {
    const extensions = apolloError?.graphQLErrors[0]?.extensions;

    const errorCode = extensions
        ? (extensions.errorCode as PreBookedErrorCode)
        : PreBookedErrorCode.ACCESS_DENIED;

    switch (errorCode) {
        case PreBookedErrorCode.ACCESS_DENIED:
            return t`Du behöver vara inloggad för att kunna avsluta en föranmälan.`;
        case PreBookedErrorCode.NOT_MEMBER_OF_TEAM:
            return t`Du kan inte avsluta en föranmälan då du inte är medlem i laget.`;
        case PreBookedErrorCode.SUBSCRIPTION_NOT_FOUND:
            return t`Du har ingen aktiv föranmälan.`;
        case PreBookedErrorCode.GENERAL_ERROR:
        default:
            return t`Tyvärr gick något fel hos oss. Försök igen om en stund!`;
    }
};

export const getPreBookedTestValue = (errorCode: string) => {
    switch (errorCode) {
        case PreBookedErrorCode.RGS_KYC_QUESTIONNAIRE_NOT_SUBMITTED:
            return "kyc-questionnaire-not-submitted";
        case PreBookedErrorCode.RGS_NETLOSS_LIMIT_EXCEEDED:
            return "net-loss-limit-exceeded";
        case PreBookedErrorCode.RGS_DEPOSIT_LIMIT_NOT_SET:
            return "deposit-limit-not-set";
        case PreBookedErrorCode.RGS_NAR_EXCLUSION:
        case PreBookedErrorCode.RGS_NAR_UNDETERMINED:
            return "nar-exclusion";
        case PreBookedErrorCode.RGS_SELF_EXCLUSION:
        case PreBookedErrorCode.RGS_TEMPORARY_SELF_EXCLUSION:
            return "self-exclusion";
        default:
            return "";
    }
};

export const isStandardAndIsCaptainOrActiveBetter = (
    team: Team,
    isActiveGameInitiator: boolean,
) => TeamUtils.isStandardTeam(team.type) && (team.isTeamCaptain || isActiveGameInitiator);

export const isCampaignOrStarterTeam = (team: Team) =>
    TeamUtils.isCampaignTeam(team.type) || TeamUtils.isBeginnerTeam(team.type);

export const isCampaignOrBeginnerAndIsCaptain = (team: Team) =>
    isCampaignOrStarterTeam(team) && team.isTeamCaptain;

export const getNavigationLinks = (
    team: Team,
    isLoggedIn: boolean,
    isActiveGameInitiator: boolean,
): TeamPageLink[] => {
    if (!isLoggedIn) return [TeamPageLink.ABOUT];

    const links: TeamPageLink[] = [TeamPageLink.ABOUT];

    const isStandardTeam = TeamUtils.isStandardTeam(team.type);

    const canShowActiveBetter = isStandardTeam || isCampaignOrBeginnerAndIsCaptain(team);
    const canShowMembers = isStandardTeam || isCampaignOrBeginnerAndIsCaptain(team);
    const canShowEmail = isStandardAndIsCaptainOrActiveBetter(
        team,
        isActiveGameInitiator,
    );
    const canShowSubscripton = isStandardTeam;

    if (canShowActiveBetter) links.push(TeamPageLink.ACTIVE_BETTER);

    if (canShowMembers) links.push(TeamPageLink.MEMBERS);

    if (canShowSubscripton) links.push(TeamPageLink.SUBSCRIPTIONS);

    if (canShowEmail) links.push(TeamPageLink.EMAIL);

    return links;
};
